import _ from 'underscore';
import React, { useCallback, useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  ActionButton,
  Stack,
  CommandButton,
  registerIcons
} from '@fluentui/react';
import classNames from 'classnames';
import { CopilotTextAssetRecommendation } from '@bingads-webui/inline-copilot';
import { getCharCountExcludingAdCustomizers } from '@bingads-webui-campaign/ad-customizers-next';
import { useEventLog, EMPTY_ASSET_HINTS, NO_FINAL_URL_HINT } from '@bingads-webui-campaign-react/copilot-common';
import { RecommendationContext } from '@bingads-webui-campaign/responsive-search-ad-recommendation';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { BaseTextField } from './base-text-field';
import { Error } from './error';
import { withError } from './with-error';
import { PinButton } from './pin-button';
import { textAssetsStyles } from './text-assets-styles';
import deleteIcon from '../media/delete.svg';

const TextAssetFields = {
  Headline: _.map(_.range(15), i => withError(BaseTextField, `Headline${i}`)),
  Description: _.map(_.range(4), i => withError(BaseTextField, `Description${i}`)),
};

registerIcons({
  icons: {
    'delete-icon': (<img style={{ position: 'absolute', left: -4 }} src={deleteIcon} alt="chatgpt-logo" />),
  },
});

const recommendationAssetTypeMap = {
  Headlines: 'headline',
  Descriptions: 'description',
};

const recommendationAssetTypePluralMap = {
  Headlines: 'headlines',
  Descriptions: 'descriptions',
};

const getStyles = () => ({
  copilotButton: {
    marginLeft: '8px',
    '& .text-asset-copilot > button': {
      height: '32px',
    },
  },
});

export function TextAssets(props) {
  const {
    config,
    assets,
    i18n,
    onChange,
    onBlur,
    isForVnext,
    updateSuggestionMeasurement,
    showDynamicTextExtention,
    llmAssetsSuggestions,
    showDeleteButton,
    showCopilotButton,
    finalUrlWithoutPrefix,
    existingAssets,
    onSuggestionsRendered,
    isNewTextAssetsButtonStyle,
    permissions,
  } = props;

  const {
    copilotButtonLabel,
    defaultSize,
    deleteButtonLabel,
    getPinItems,
    inputLabel,
    itemProperty,
    maxSize,
    property,
    enableDKI,
    DKIThemeName,
  } = config;

  const [recommendationContext] = useContext(RecommendationContext);
  const { postAssetEventLog = _.noop, themes, copilotTracking } = recommendationContext;
  const {
    logTextAssetMenuOpen,
    logTextAssetDialogOpen,
    logRefineTextAsset,
    logTextAssetApplied,
    logRemoveSingleAsset,
    logEditSingleAsset,
  } = useEventLog(postAssetEventLog);

  const [cachedAssets, setCachedAssets] = useState(assets);
  const classes = useDefaultStyles(getStyles);

  useEffect(() => setCachedAssets(assets), [assets]);

  useEffect(() => {
    if (_.size(cachedAssets) > maxSize) {
      setCachedAssets(cachedAssets.slice(0, maxSize));
    } else if (_.size(cachedAssets) < defaultSize) {
      const filledAssets = [...cachedAssets];
      for (let i = 0; i < defaultSize - _.size(cachedAssets); i += 1) {
        filledAssets.push({
          Asset: {
            '@odata.type': '#Model.TextAsset',
            AssetType: 'Text',
            Name: null,
            Text: '',
          },
          PinnedField: 'None',
        });
      }
      setCachedAssets(filledAssets);
    }
  }, [cachedAssets, maxSize, defaultSize]);

  const onAddAsset = useCallback(() => {
    if (cachedAssets.length < maxSize) {
      const newAssets = [
        ...cachedAssets,
        {
          Asset: {
            '@odata.type': '#Model.TextAsset',
            AssetType: 'Text',
            Name: null,
            Text: '',
          },
          PinnedField: 'None',
        },
      ];
      setCachedAssets(newAssets);
      onChange(newAssets, property);
    }
  }, [cachedAssets, maxSize, onChange, property]);

  const onTextChange = useCallback((index, value, key, populatedWithFullValue) => {
    const newAssets = [...cachedAssets];
    const deletedAsset = newAssets[index].Asset.Text;
    const pluralAssetType = recommendationAssetTypePluralMap[property];

    newAssets[index].Asset.Text = value;
    setCachedAssets(newAssets);
    onChange(newAssets, `${itemProperty}${index}`);

    if (copilotTracking.isInRemainingInitialSuggestions(itemProperty, deletedAsset)) {
      copilotTracking.removeInitialSuggestion(itemProperty, deletedAsset);

      const recommendationId = _.chain(themes)
        .map(theme => _.chain(theme)
          .get(pluralAssetType, [])
          .find(asset => asset.Text === deletedAsset)
          .get('RecommendationId')
          .value())
        .compact()
        .first()
        .value();

      if (recommendationId) {
        logEditSingleAsset(itemProperty, recommendationId);
      }
    }

    // populating suggestion will not trigger onBlur, so needs to handle specially.
    if (populatedWithFullValue) {
      onBlur(newAssets, `${itemProperty}${index}`);
    }
  }, [cachedAssets, property, onChange, itemProperty, copilotTracking, themes, logEditSingleAsset, onBlur]);

  const onTextBlur = useCallback((index) => {
    onBlur(cachedAssets, `${itemProperty}${index}`);
  }, [cachedAssets, itemProperty, onBlur]);

  const onPinChange = useCallback((index, value) => {
    const newAssets = [...cachedAssets];
    newAssets[index].PinnedField = value;
    setCachedAssets(newAssets);
    onChange(newAssets, property);
  }, [cachedAssets, onChange, property]);

  const onDeleteAsset = useCallback((index) => {
    const newAssets = [...cachedAssets];
    const deletedAsset = newAssets[index].Asset.Text;
    const pluralAssetType = recommendationAssetTypePluralMap[property];

    if (cachedAssets.length > defaultSize) {
      newAssets.splice(index, 1);
    } else {
      for (let i = index; i < newAssets.length - 1; i += 1) {
        newAssets[i].Asset.Text = newAssets[i + 1].Asset.Text;
      }
      newAssets[newAssets.length - 1].Asset.Text = '';
    }
    setCachedAssets(newAssets);
    onChange(newAssets, property);

    if (!copilotTracking.isInRemainingInitialSuggestions(itemProperty, deletedAsset)) { return; }
    copilotTracking.removeInitialSuggestion(itemProperty, deletedAsset);

    const recommendationId = _.chain(themes)
      .map(theme => _.chain(theme)
        .get(pluralAssetType, [])
        .find(asset => asset.Text === deletedAsset)
        .get('RecommendationId')
        .value())
      .compact()
      .first()
      .value();

    if (recommendationId) {
      logRemoveSingleAsset(itemProperty, recommendationId);
    }
  }, [cachedAssets, property, defaultSize, onChange, themes, logRemoveSingleAsset, itemProperty, copilotTracking]);

  const isPinningEnabled = !_.isUndefined(getPinItems);

  const assetType = recommendationAssetTypeMap[property];
  const calculateDisableReason = (text) => {
    if (_.isEmpty(text.trim())) {
      return i18n.getString(EMPTY_ASSET_HINTS[assetType]);
    }
    if (!finalUrlWithoutPrefix) {
      return i18n.getString(NO_FINAL_URL_HINT);
    }
    if (getCharCountExcludingAdCustomizers({
      text,
      adType: 'ResponsiveSearchAd',
      field: property,
    }) !== text.length) {
      return i18n.getString(_TL_('Microsoft Advertising can\'t use dynamic texts for generating asset recommendations. Please enter static text and try again.'));
    }
    return null;
  };

  return (
    <Stack
      className={config.className}
      tokens={textAssetsStyles.textAssetsStackToken}
    >
      {_.map(cachedAssets, (asset, index) => {
        const TextAsset = TextAssetFields[itemProperty][index];

        return (
          <Stack key={`asset-${property}-${index}`}>
            <Stack horizontal>
              <Stack.Item grow>
                <TextAsset
                  assets={cachedAssets}
                  configProps={{
                    key: property,
                    className: config.itemClassName,
                    ariaLabel: i18n.getString(inputLabel, { index: index + 1 }),
                  }}
                  value={asset.Asset.Text}
                  updateItem={_.partial(onTextChange, index)}
                  i18n={i18n}
                  optional
                  showCharRemaining
                  showErrorMessage
                  isForVnext={isForVnext}
                  index={index}
                  updateSuggestionMeasurement={updateSuggestionMeasurement}
                  showDynamicTextExtention={showDynamicTextExtention}
                  onBlur={_.partial(onTextBlur, index)}
                  llmAssetsSuggestions={llmAssetsSuggestions}
                  enableDKI={permissions && permissions.dynamic && permissions.dynamic.CopilotDKIRecommendation && enableDKI}
                  DKIThemeName={DKIThemeName}
                />
              </Stack.Item>
              {showCopilotButton && (
                <div className={classNames('copilot-button', classes.copilotButton)}>
                  <CopilotTextAssetRecommendation
                    assetType={assetType}
                    currentIndex={index}
                    disableReason={calculateDisableReason(cachedAssets[index].Asset.Text)}
                    existingAssets={existingAssets}
                    menuButtonAriaLabel={i18n.getString(copilotButtonLabel, { index: index + 1 })}
                    onMenuOpen={logTextAssetMenuOpen}
                    onOperationDialogOpen={logTextAssetDialogOpen}
                    onFetch={logRefineTextAsset}
                    onSelect={(newAsset, operationName) => {
                      const text = _.get(newAsset, ['value', 0, 'text']);
                      logTextAssetApplied(assetType, operationName);
                      onTextChange(index, text, property);
                    }}
                    onSuggestionsRendered={onSuggestionsRendered}
                  />
                </div>
              )}
              {showDeleteButton && (
                <CommandButton
                  className={`rsa-delete-button ${property}-${index}`}
                  iconProps={{ iconName: 'delete-icon' }}
                  styles={isNewTextAssetsButtonStyle ? textAssetsStyles.newPositionDeleteButton : textAssetsStyles.newDeleteButton}
                  onClick={() => onDeleteAsset(index)}
                  ariaLabel={i18n.getString(deleteButtonLabel, { index: index + 1 })}
                />
              )}

              {isPinningEnabled && (
                <PinButton
                  className={`asset-pin-btn ${property}-${index}`}
                  getPinItems={getPinItems}
                  pinnedPosition={asset.PinnedField}
                  styles={isNewTextAssetsButtonStyle ? textAssetsStyles.newPositionPinButton : textAssetsStyles.newPinButton}
                  i18n={i18n}
                  onChange={_.partial(onPinChange, index)}
                />
              )}
            </Stack>
          </Stack>
        );
      })}

      <Error property={property} />

      <ActionButton
        className="add-asset-btn"
        iconProps={{ iconName: 'Add' }}
        allowDisabledFocus
        disabled={cachedAssets.length >= maxSize}
        onClick={onAddAsset}
      >
        {i18n.getString(config.addLabel)}
      </ActionButton>
    </Stack>
  );
}

TextAssets.propTypes = {
  config: PropTypes.shape({
    addLabel: PropTypes.string.isRequired,
    className: PropTypes.string,
    copilotButtonLabel: PropTypes.string.isRequired,
    defaultSize: PropTypes.number.isRequired,
    deleteButtonLabel: PropTypes.string.isRequired,
    getPinItems: PropTypes.func,
    inputLabel: PropTypes.string,
    itemClassName: PropTypes.string,
    itemProperty: PropTypes.string,
    maxSize: PropTypes.number.isRequired,
    property: PropTypes.string,
    enableDKI: PropTypes.bool,
    DKIThemeName: PropTypes.string,
  }).isRequired,

  assets: PropTypes.arrayOf(PropTypes.shape({
    Asset: PropTypes.shape({
      Text: PropTypes.string.isRequired,
    }).isRequired,
    PinnedField: PropTypes.string.isRequired,
  })),

  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,

  onChange: PropTypes.func,
  onBlur: PropTypes.func,

  isForVnext: PropTypes.bool.isRequired,

  updateSuggestionMeasurement: PropTypes.func.isRequired,

  showDynamicTextExtention: PropTypes.bool.isRequired,

  llmAssetsSuggestions: PropTypes.shape({
    Suggestions: PropTypes.arrayOf(PropTypes.shape({
      SuggestionId: PropTypes.string,
      Asset: PropTypes.string,
    })),
  }),
  showDeleteButton: PropTypes.bool,
  finalUrlWithoutPrefix: PropTypes.string,
  showCopilotButton: PropTypes.bool,
  existingAssets: PropTypes.shape({}),
  onSuggestionsRendered: PropTypes.func,
  isNewTextAssetsButtonStyle: PropTypes.bool,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      CopilotDKIRecommendation: PropTypes.bool,
    }),
  }),
};

TextAssets.defaultProps = {
  assets: [],
  onChange: _.noop,
  onBlur: _.noop,
  llmAssetsSuggestions: {},
  showDeleteButton: false,
  finalUrlWithoutPrefix: '',
  showCopilotButton: false,
  existingAssets: {},
  onSuggestionsRendered: _.noop,
  isNewTextAssetsButtonStyle: false,
  permissions: {},
};
